import React from "react";
import { Button, ButtonProps } from "@mui/material";
import { motion } from "framer-motion";

export type AppButtonVariant = "primary" | "secondary" | "text" | "danger";

export interface AppButtonProps extends Omit<ButtonProps, "variant"> {
  /** primary = contained brand, secondary = outlined brand, text = text, danger = contained error */
  variant?: AppButtonVariant;
  /** MUI color (e.g. "error" for secondary logout); overrides variant default when set */
  color?: ButtonProps["color"];
  /** Enable subtle hover/tap motion (default true for primary/secondary) */
  withMotion?: boolean;
}

const motionProps = {
  whileHover: { scale: 1.02 },
  whileTap: { scale: 0.98 },
  transition: { duration: 0.15 }
};

const AppButton = React.forwardRef<HTMLButtonElement, AppButtonProps>(
  function AppButton(
    {
      variant = "primary",
      color: colorProp,
      withMotion,
      children,
      sx,
      ...rest
    },
    ref
  ) {
    const useMotion =
      withMotion ?? (variant === "primary" || variant === "secondary");

    const variantStyles = {
      primary: {
        variant: "contained" as const,
        color: (colorProp ?? "primary") as ButtonProps["color"],
        sx: {
          textTransform: "none",
          borderRadius: 2,
          fontWeight: 600,
          boxShadow: "0 2px 8px rgba(111, 73, 201, 0.25)",
          "&:hover": {
            boxShadow: "0 4px 12px rgba(111, 73, 201, 0.35)"
          },
          ...sx
        }
      },
      secondary: {
        variant: "outlined" as const,
        color: (colorProp ?? "primary") as ButtonProps["color"],
        sx: {
          textTransform: "none",
          borderRadius: 2,
          fontWeight: 600,
          borderWidth: 1.5,
          "&:hover": {
            borderWidth: 1.5
          },
          ...sx
        }
      },
      text: {
        variant: "text" as const,
        color: "primary" as const,
        sx: {
          textTransform: "none",
          fontWeight: 600,
          ...sx
        }
      },
      danger: {
        variant: "contained" as const,
        color: "error" as const,
        sx: {
          textTransform: "none",
          borderRadius: 2,
          fontWeight: 600,
          "&:hover": { opacity: 0.92 },
          ...sx
        }
      }
    };

    const config = variantStyles[variant];
    const button = (
      <Button
        ref={ref}
        variant={config.variant}
        color={colorProp ?? config.color}
        sx={config.sx}
        {...rest}
      >
        {children}
      </Button>
    );

    if (useMotion) {
      return (
        <motion.div style={{ display: "inline-block" }} {...motionProps}>
          {button}
        </motion.div>
      );
    }

    return button;
  }
);

export default AppButton;
